import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { _internals } from "../services/alarmService";

/**
 * useAlarmNotificationListener — registra un listener globale per il tap
 * sulla notifica di sveglia. Quando l'utente tocca la notifica:
 *   1) il sistema apre l'app sull'ultima route attiva
 *   2) il plugin emette `localNotificationActionPerformed`
 *   3) noi navighiamo a `/alarm-ringing?sound=<id>` che fa partire il loop.
 *
 * Idempotente: rimuoviamo tutti i listener prima di aggiungerli (workaround
 * per i casi in cui solo l'ultima istanza resta attiva — vedi playbook).
 */
export default function useAlarmNotificationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (platform !== "ios" && platform !== "android") return undefined;

    let mounted = true;
    let actionHandle = null;
    let receivedHandle = null;

    (async () => {
      await LocalNotifications.removeAllListeners();

      actionHandle = await LocalNotifications.addListener(
        "localNotificationActionPerformed",
        (action) => {
          if (!mounted) return;
          const extra = action?.notification?.extra || {};
          if (!extra[_internals.EXTRA_KEY]) return;
          const soundId = extra.alarmSoundId || "rain";
          navigate(`/alarm-ringing?sound=${encodeURIComponent(soundId)}`);
        }
      );

      // Quando la notifica arriva in foreground iOS la mostra comunque (banner)
      // ma non navighiamo: vogliamo che l'utente la tocchi prima.
      receivedHandle = await LocalNotifications.addListener(
        "localNotificationReceived",
        () => {
          /* no-op: gestito da iOS, l'utente decide se aprire */
        }
      );
    })();

    return () => {
      mounted = false;
      if (actionHandle && typeof actionHandle.remove === "function") {
        actionHandle.remove();
      }
      if (receivedHandle && typeof receivedHandle.remove === "function") {
        receivedHandle.remove();
      }
    };
  }, [navigate]);
}
